import React, { useState } from 'react';
import axios from 'axios';
import { X, Upload, Loader2, CheckCircle2, FileText, ImageIcon, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_MB = 8;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const PayReceiptModal = ({ investment, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error('Please upload a JPG, PNG, WebP or PDF');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Receipt too large (max ${MAX_MB}MB)`);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const form = new FormData();
      if (file) form.append('receipt', file);
      if (note) form.append('note', note);
      await axios.post(`${API}/investments/${investment._id}/mark-paid`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Payment reported — we\'ll verify and activate shortly.');
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to report payment');
    } finally {
      setSubmitting(false);
    }
  };

  const FileIcon = file?.type === 'application/pdf' ? FileText : ImageIcon;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pay-receipt-title"
      data-testid="pay-receipt-modal"
    >
      <Card className="w-full max-w-md my-8">
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-t-xl relative">
          <button
            type="button"
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={onClose}
            aria-label="Close"
            data-testid="pay-receipt-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 id="pay-receipt-title" className="text-lg font-bold">I&apos;ve paid — verify my transfer</h2>
          <p className="text-green-100 text-xs">Ref: {investment.application_number}</p>
        </div>

        <CardContent className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 text-xs">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-amber-900">
              Upload a screenshot / PDF of the transfer receipt so admins can confirm faster.
              This is optional but strongly recommended.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Receipt (optional)
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-green-500 transition-colors bg-gray-50">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={onFile}
                className="hidden"
                data-testid="receipt-file-input"
              />
              {file ? (
                <div className="text-center">
                  <FileIcon className="h-8 w-8 text-green-600 mx-auto mb-1" aria-hidden="true" />
                  <p className="text-sm font-medium truncate max-w-full">{file.name}</p>
                  <p className="text-[11px] text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB · click to change
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-1" aria-hidden="true" />
                  <p className="text-sm font-medium">Click to upload</p>
                  <p className="text-[11px] text-gray-500">JPG · PNG · WebP · PDF (max {MAX_MB}MB)</p>
                </div>
              )}
            </label>
          </div>

          <div>
            <label htmlFor="receipt-note" className="block text-sm font-medium text-gray-700 mb-1">
              Note to admin (optional)
            </label>
            <textarea
              id="receipt-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Paid from XYZ Bank account"
              className="w-full border rounded-md p-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500"
              maxLength={500}
              data-testid="receipt-note-input"
            />
            <p className="text-[11px] text-gray-400 text-right">{note.length} / 500</p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="pay-receipt-cancel-btn">
              Cancel
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={submit}
              disabled={submitting}
              data-testid="pay-receipt-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              {submitting ? 'Reporting…' : 'Report payment'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayReceiptModal;
